import React from 'react';
import {
  Grid,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  IconButton,
  Stack,
  Avatar,
  CircularProgress,
  Typography,
  Chip,
  Menu,
  MenuItem,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AddIcon from '@mui/icons-material/Add';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { format, parseISO } from 'date-fns';
import Scrollbar from '../scrollbar';

const TABLE_HEAD = [
  { id: 'name', label: 'Account Name', alignRight: false },
  { id: 'account_type', label: 'Type', alignRight: false },
  { id: 'balance', label: 'Balance', alignRight: false },
  { id: 'currency', label: 'Currency', alignRight: false },
  { id: 'is_active', label: 'Status', alignRight: false },
  { id: 'created_at', label: 'Created', alignRight: false },
  { id: 'actions', label: 'Actions', alignRight: false },
];

const MoneyAccountTable = ({
  accounts,
  isLoading,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  onView,
  onEdit,
  onDelete,
  onAddIncome,
  onAddExpense,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedAccount, setSelectedAccount] = React.useState(null);

  const handleMenuClick = (event, account) => {
    setAnchorEl(event.currentTarget);
    setSelectedAccount(account);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAccount(null);
  };

  const handleAddIncome = () => {
    if (selectedAccount && onAddIncome) {
      onAddIncome(selectedAccount);
    }
    handleMenuClose();
  };

  const handleAddExpense = () => {
    if (selectedAccount && onAddExpense) {
      onAddExpense(selectedAccount);
    }
    handleMenuClose();
  };

  return (
    <Grid item xs={12}>
      <Card>
        <Scrollbar>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {TABLE_HEAD.map((headCell) => (
                    <TableCell key={headCell.id} align={headCell.alignRight ? 'right' : 'left'}>
                      {headCell.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              {isLoading ? (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={TABLE_HEAD.length} sx={{ py: 3 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (accounts || []).length === 0 ? (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={TABLE_HEAD.length} sx={{ py: 3 }}>
                      <Paper sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="body2">No money accounts found.</Typography>
                      </Paper>
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <TableBody>
                  {accounts.map((account) => (
                    <TableRow hover key={account.id}>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar>
                            <AccountBalanceWalletIcon />
                          </Avatar>
                          <Typography variant="subtitle2">{account.name}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{account.account_type}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color={account.balance >= 0 ? 'success.main' : 'error.main'}>
                          {account.currency}{' '}
                          {Number(account.balance || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </Typography>
                      </TableCell>
                      <TableCell>{account.currency}</TableCell>
                      <TableCell>
                        <Chip
                          label={account.is_active ? 'Active' : 'Inactive'}
                          color={account.is_active ? 'success' : 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {account.created_at ? format(parseISO(account.created_at), 'MMM dd, yyyy') : '-'}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <IconButton size="small" onClick={() => onView(account)} sx={{ color: 'primary.main' }}>
                            <VisibilityIcon />
                          </IconButton>
                          <IconButton size="small" onClick={() => onEdit(account)} sx={{ color: 'primary.main' }}>
                            <EditIcon />
                          </IconButton>
                          <IconButton size="small" onClick={() => onDelete(account)} sx={{ color: 'error.main' }}>
                            <DeleteIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuClick(e, account)}
                            sx={{ color: 'success.main' }}
                          >
                            <AddIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Scrollbar>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </Card>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleAddIncome}>
          <TrendingUpIcon sx={{ mr: 1, color: 'success.main' }} />
          Add Income
        </MenuItem>
        <MenuItem onClick={handleAddExpense}>
          <TrendingDownIcon sx={{ mr: 1, color: 'error.main' }} />
          Add Expense
        </MenuItem>
      </Menu>
    </Grid>
  );
};

export default MoneyAccountTable;
